import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { 
  Monitor, 
  Plus, 
  Search, 
  Filter, 
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Calendar,
  Link,
  Image,
  Video,
  SquareStack,
  Volume2,
  VolumeX,
  CheckCircle,
  Clock,
  TrendingUp,
  Smartphone
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useWatch } from '@/contexts/WatchContext'
import CreateImageModal from './CreateImageModal'
import EditImageModal from './EditImageModal'
import EditVideoModal from './EditVideoModal'
import CreateSlideshowModal from './CreateSlideshowModal'
import EditSlideshowModal from './EditSlideshowModal'
import CreateDynamicContentModal from './CreateDynamicContentModal'
import screenService from '@/utils/services/ScreenService.js'
import deviceService from '@/utils/services/DeviceService.js'
import { toast } from 'react-hot-toast'
import ScreenPreviewModal from './ScreenPreviewModal'
import CreateVideoModal from './CreateVideoModal'
import { canCreateScreens, canCreateVideoClips, isFreePlanUser, hasActiveSubscription, canAssignScreenToDevice, countDevicesRunningScreens, getDeviceLimit } from '@/utils/subscriptionUtils'
import { hasAdminPrivileges } from '@/utils/adminUtils'
import SubscriptionUpgradeModal from '@/components/UI/SubscriptionUpgradeModal'
import DeviceLimitModal from '@/components/UI/DeviceLimitModal'
import AddScheduleModal from '../Schedule/AddScheduleModal'
import ModifyScheduleModal from '../Schedule/ModifyScheduleModal'
import useGoogleAnalytics from '@/hooks/useGoogleAnalytics'
import SubscriptionService from '@/utils/services/SubscriptionService'
import userService from '@/utils/services/UserService'

// --- Hooks ---
function useScreens(uid) {
  const [screens, setScreens] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  useEffect(() => {
    if (!uid) {
      setLoading(false)
      setScreens([])
      setError(null)
      return
    }
    
    setLoading(true)
    setError(null)
    
    const fetchScreens = async () => {
      try {
        const screensData = await screenService.getUserScreens(uid)
        setScreens(screensData)
      } catch (error) {
        console.error('Error fetching screens:', error)
        setError(error.message)
        setScreens([])
      } finally {
        setLoading(false)
      }
    }
    
    fetchScreens()
  }, [uid])
  
  return { screens, loading, error, setScreens }
}

function useDevices(uid) {
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  
  const fetchDevices = async () => {
    if (!uid) {
      setDevices([])
      setLoading(false)
      return
    }
    
    try {
      setLoading(true)
      const devicesData = await deviceService.getUserDevices(uid)
      setDevices(devicesData)
    } catch (error) {
      console.error('Error fetching devices:', error)
      setDevices([])
    } finally {
      setLoading(false)
    }
  }
  
  useEffect(() => {
    fetchDevices()
  }, [uid])
  
  return { devices, loading, setDevices, fetchDevices }
}

// --- Main Page ---
const Screens = () => {
  const { currentUser, currentUserData } = useAuth()
  const { getEffectiveUserId } = useWatch()
  const location = useLocation()
  const effectiveUserId = getEffectiveUserId()
  const { screens, loading, error, setScreens } = useScreens(effectiveUserId)
  const { devices, loading: devicesLoading, setDevices, fetchDevices } = useDevices(effectiveUserId)
  const [showAdd, setShowAdd] = useState(false)
  
  // Debug showAdd state changes
  useEffect(() => {
    console.log('🔍 showAdd state changed to:', showAdd, 'type:', typeof showAdd);
  }, [showAdd]);
  const [showEdit, setShowEdit] = useState(null)
  const [showDelete, setShowDelete] = useState(null)
  const [showPreview, setShowPreview] = useState(null)
  const [showAssign, setShowAssign] = useState(null)
  const [showSchedule, setShowSchedule] = useState(null)
  const [showAddSchedule, setShowAddSchedule] = useState(null)
  const [showModifySchedule, setShowModifySchedule] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState({ show: false, feature: null })
  const [showNoDevicesModal, setShowNoDevicesModal] = useState(false)
  const [showDeviceLimitModal, setShowDeviceLimitModal] = useState(false)
  
  // State for subscription data
  const [subscriptions, setSubscriptions] = useState([])
  const [userSubscriptions, setUserSubscriptions] = useState([])
  
  // Google Analytics tracking
  const { trackContentManagement, trackFeatureUsage, trackError } = useGoogleAnalytics()

  // Prevent body scroll when modals are open
  useEffect(() => {
    if (showDelete || showUpgradeModal.show || showNoDevicesModal || showDeviceLimitModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [showDelete, showUpgradeModal, showNoDevicesModal, showDeviceLimitModal])

  // Handle incoming navigation state to auto-open modals
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setShowAdd(location.state.openCreateModal)
      // Store additional data for modals (screenName, imageUrl, etc.)
      if (location.state.screenName) {
        sessionStorage.setItem('prefillScreenName', location.state.screenName)
      }
      if (location.state.imageUrl) {
        sessionStorage.setItem('prefillImageUrl', location.state.imageUrl)
      }
      // Clear the state to prevent reopening on subsequent renders
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  // Handle AI agent preview requests
  useEffect(() => {
    if (location.state?.previewScreen) {
      const screenKey = location.state.previewScreen
      const screen = screens.find(s => s.key === screenKey)
      if (screen) {
        setShowPreview(screen)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, screens])

  // Handle AI agent edit screen requests
  useEffect(() => {
    if (location.state?.editScreen) {
      const screenKey = location.state.editScreen
      const screen = screens.find(s => s.key === screenKey)
      if (screen) {
        setShowEdit(screen)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, screens])

  // Handle AI agent delete screen requests
  useEffect(() => {
    if (location.state?.deleteScreen) {
      const screenKey = location.state.deleteScreen
      const screen = screens.find(s => s.key === screenKey)
      if (screen) {
        setShowDelete(screen)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, screens])

  // Listen for AI agent refresh events
  useEffect(() => {
    const handleScreensRefresh = (event) => {
      console.log('🔄 Screens refresh event received from AI agent:', event.detail)
      // Force refresh of screens data
      if (effectiveUserId) {
        const fetchScreens = async () => {
          try {
            const screensData = await screenService.getUserScreens(effectiveUserId, false) // Force fresh data
            setScreens(screensData)
            console.log('✅ Screens data refreshed after AI agent deletion')
          } catch (error) {
            console.error('❌ Error refreshing screens after AI agent deletion:', error)
          }
        }
        fetchScreens()
      }
    }

    // Add event listener
    window.addEventListener('screens-refresh', handleScreensRefresh)

    // Cleanup
    return () => {
      window.removeEventListener('screens-refresh', handleScreensRefresh)
    }
  }, [effectiveUserId, setScreens])

  // Load subscription data
  useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        // Load subscriptions and user subscriptions
        const [subscriptionsData, userSubs] = await Promise.all([
          SubscriptionService.loadSubscriptions(true),
          userService.getUserSubscriptions(currentUser.uid, true)
        ])
        
        // Merge subscription data into currentUserData for easier access
        if (currentUserData) {
          currentUserData.subscriptions = userSubs;
          currentUserData.availableSubscriptions = subscriptionsData;
        }
        
        setSubscriptions(subscriptionsData)
        setUserSubscriptions(userSubs)
      } catch (error) {
        console.error('Error loading subscription data:', error)
      }
    }

    if (currentUser && currentUserData) {
      loadSubscriptionData()
    }
  }, [currentUser, currentUserData])

  // Get screen thumbnail URL
  const getScreenThumb = (screen) => {
    if (!screen) return null
    
    // Check for video thumbnail first
    if (screen.videoUrl) {
      return screen.thumbUniversalUrl || screen.thumbUrl || screen.videoUrl
    }
    
    // Check for slides (first slide)
    if (screen.slides && screen.slidesData && screen.slidesData[0]) {
      const firstSlide = screen.slidesData[0]
      return firstSlide.imageUniversalUrl || firstSlide.imageUrl || firstSlide.thumbUniversalUrl || firstSlide.thumbUrl || firstSlide.videoUrl
    }
    
    // Check for regular image
    if (screen.imageUrl) {
      return screen.imageUniversalUrl || screen.imageUrl
    }
    
    return null
  }

  // Get thumbnail for display
  const getScreenThumbById = (screen) => {
    const thumbUrl = getScreenThumb(screen)
    if (!thumbUrl || typeof thumbUrl !== 'string') return null
    
    // For Cloudflare URLs, return as-is since they don't have the same naming pattern
    if (thumbUrl.includes('r2.dev') || thumbUrl.includes('cloudflare')) {
      return thumbUrl;
    }
    
    // Convert to thumbnail size for Firebase URLs
    return thumbUrl
      .replace("_1920x1080.", "_640x360.")
      .replace("_1920x1920.", "_640x640.")
      .replace('.mp4', '_640x360.jpeg')

  }

  // Get aspect ratio
  const getAspectRatio = (screen) => {
    // Always return 16/9 to keep the same height regardless of angle
    return '16/9'
  }

  // Get screen type icon
  const getScreenIcon = (screen) => {
    if (screen.isDynamic) {
      return <TrendingUp className="w-4 h-4" />
    } else if (screen.videoUrl) {
      return <Video className="w-4 h-4" />
    } else if (screen.slides && screen.slidesData && screen.slidesData.length > 0) {
      return <SquareStack className="w-4 h-4" />
    } else {
      return <Image className="w-4 h-4" />
    }
  }

  // Get screen type label
  const getScreenType = (screen) => {
    if (screen.isDynamic) {
      return `Dynamic ${screen.contentType ? screen.contentType.charAt(0).toUpperCase() + screen.contentType.slice(1) : 'Content'}`
    } else if (screen.videoUrl) {
      return 'Video Clip'
    } else if (screen.slides && screen.slidesData && screen.slidesData.length > 0) {
      return 'Slideshow'
    } else {
      return 'Single Image'
    }
  }

  // Delete screen
  const handleDelete = async (screen) => {
    console.log('🗑️ Starting screen deletion process:', {
      screenKey: screen.key,
      screenName: screen.name,
      screenType: getScreenType(screen),
      hasVideoUrl: !!screen.videoUrl,
      hasImageUrl: !!screen.imageUrl,
      hasSlides: !!(screen.slides && screen.slidesData),
      userId: currentUser?.uid
    });
    
    setIsDeleting(true)
    setDeleteError(null)
    try {
      await screenService.deleteScreen(screen.key, currentUser?.uid);
      console.log('✅ Screen deletion completed successfully');
      
      setShowDelete(null);
      setScreens(screens.filter(s => s.key !== screen.key));
      toast.success(`Screen "${screen.name}" deleted successfully!`)
      
      // Track screen deletion
      trackContentManagement('delete_screen', getScreenType(screen))
    } catch (err) {
      console.error('❌ Screen deletion failed:', {
        error: err.message,
        code: err.code,
        stack: err.stack,
        screenKey: screen.key,
        screenName: screen.name
      });
      
      setDeleteError(err.message || 'Failed to delete screen')
      toast.error(`Failed to delete screen: ${err.message || ''}`)
      
      // Track deletion error
      trackError('screen_deletion_error', err.message, err.code)
    } finally {
      setIsDeleting(false)
    }
  }

  // Assign screen to device
  const handleAssign = async (deviceKey) => {
    try {
      console.log('Assigning screen:', { screenKey: showAssign.key, deviceKey })
      
      // Check if user can assign screen to device (considering device limits for screens only)
      const deviceLimitCheck = canAssignScreenToDevice(currentUserData, devices, userSubscriptions, subscriptions, deviceKey)
      
      if (!deviceLimitCheck.canAssign) {
        console.log('Device limit reached for screens:', deviceLimitCheck.reason)
        setShowAssign(null) // Close the current assignment modal
        setShowDeviceLimitModal(true)
        return
      }
      
      // Update the device's screenId using DeviceService
      await deviceService.assignScreenToDevice(deviceKey, showAssign.key)
      
      console.log('Device assignment completed successfully')
      
      setShowAssign(null)
      
      // Show success message
      console.log(`Screen "${showAssign.name}" assigned to device successfully`)
      
      // Track screen assignment
      trackContentManagement('assign_screen_to_device', getScreenType(showAssign))
      
      // Refresh devices list to show updated assignments
      await fetchDevices()
      
    } catch (error) {
      console.error('Error assigning screen to device:', error)
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      })
      
      // Track assignment error
      trackError('screen_assignment_error', error.message, error.code)
      
      throw error // Re-throw to show in the modal
    }
  }

  // Handle schedule button click
  const handleScheduleClick = (screen) => {
    // Find devices that have this screen assigned
    const assignedDevices = devices.filter(device => device.screenId === screen.key)
    
    if (assignedDevices.length === 0) {
      // No devices assigned to this screen
      setShowSchedule(screen)
    } else {
      const device = assignedDevices[0]
      
      // Check if device has existing schedules
      if (device.schedule && device.schedule.length > 0) {
        // Show modify schedule modal for the first schedule
        setShowModifySchedule({
          screen: screen,
          device: device,
          scheduleToEdit: device.schedule[0] // Edit the first schedule
        })
      } else {
        // Show add schedule modal for the device
        setShowAddSchedule({
          screen: screen,
          device: device
        })
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Screens</h1>
          <p className="text-gray-600">Manage your digital signage screens and content</p>
        </div>
        <div className="flex gap-2">
          <button 
            className="flex items-center px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            onClick={() => {
                          if (canCreateScreens(currentUserData, screens.length, userSubscriptions, subscriptions)) {
              setShowAdd('image')
              trackContentManagement('create_screen', 'image')
            } else {
                setShowUpgradeModal({ show: true, feature: 'screens' })
                trackFeatureUsage('subscription_upgrade_required', 'create_image_screen')
              }
            }}
          >
            <Image className="w-4 h-4 mr-2" />
            Single Image
          </button>
          <button 
            className="flex items-center px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            onClick={() => {
              if (canCreateScreens(currentUserData, screens.length, userSubscriptions, subscriptions)) {
                setShowAdd('slides')
                trackContentManagement('create_screen', 'slideshow')
              } else {
                setShowUpgradeModal({ show: true, feature: 'screens' })
                trackFeatureUsage('subscription_upgrade_required', 'create_slideshow_screen')
              }
            }}
          >
            <SquareStack className="w-4 h-4 mr-2" />
            Slideshow
          </button>
          <button 
            className="flex items-center px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            onClick={() => {
              if (canCreateVideoClips(currentUserData)) {
                if (canCreateScreens(currentUserData, screens.length, userSubscriptions, subscriptions)) {
                  setShowAdd('video')
                  trackContentManagement('create_screen', 'video')
                } else {
                  setShowUpgradeModal({ show: true, feature: 'video_clips' })
                  trackFeatureUsage('subscription_upgrade_required', 'create_video_screen')
                }
              } else {
                setShowUpgradeModal({ show: true, feature: 'video_clips' })
                trackFeatureUsage('subscription_upgrade_required', 'create_video_screen')
              }
            }}
          >
            <Video className="w-4 h-4 mr-2" />
            Video Clip
          </button>
          <button 
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            onClick={() => {
              if (isFreePlanUser(currentUserData)) {
                setShowUpgradeModal({ show: true, feature: 'ai_features' })
                trackFeatureUsage('subscription_upgrade_required', 'create_dynamic_screen')
              } else if (canCreateScreens(currentUserData, screens.length, userSubscriptions, subscriptions)) {
                setShowAdd('dynamic')
                trackContentManagement('create_screen', 'dynamic')
              } else {
                setShowUpgradeModal({ show: true, feature: 'screens' })
                trackFeatureUsage('subscription_upgrade_required', 'create_dynamic_screen')
              }
            }}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Dynamic Content
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading screens...</div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 mb-4">Error loading screens: {error}</div>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              Retry
            </button>
          </div>
        ) : screens.length === 0 ? (
          <div className="text-center py-12">
            <Monitor className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No screens yet</h3>
            <p className="text-gray-600 mb-6">Create your first digital signage screen to get started</p>
            <div className="flex gap-2 justify-center">
              <button 
                className="flex items-center px-4 py-2 rounded-lg transition-colors bg-yellow-500 text-white hover:bg-yellow-600"
                onClick={() => {
                  if (canCreateScreens(currentUserData, screens.length, userSubscriptions, subscriptions)) {
                    setShowAdd('image')
                  } else {
                    setShowUpgradeModal({ show: true, feature: 'screens' })
                  }
                }}
              >
                <Image className="w-4 h-4 mr-2" />
                Single Image
              </button>
              <button 
                className="flex items-center px-4 py-2 rounded-lg transition-colors bg-yellow-500 text-white hover:bg-yellow-600"
                onClick={() => {
                  if (canCreateScreens(currentUserData, screens.length, userSubscriptions, subscriptions)) {
                    setShowAdd('slides')
                  } else {
                    setShowUpgradeModal({ show: true, feature: 'screens' })
                  }
                }}
              >
                <SquareStack className="w-4 h-4 mr-2" />
                Slideshow
              </button>
              <button 
                className="flex items-center px-4 py-2 rounded-lg transition-colors bg-yellow-500 text-white hover:bg-yellow-600"
                onClick={() => {
                  if (isFreePlanUser(currentUserData)) {
                    if (canCreateScreens(currentUserData, screens.length, userSubscriptions, subscriptions)) {
                      setShowAdd('video')
                    } else {
                      setShowUpgradeModal({ show: true, feature: 'video_clips' })
                    }
                  } else {
                    setShowUpgradeModal({ show: true, feature: 'video_clips' })
                  }
                }}
              >
                <Video className="w-4 h-4 mr-2" />
                Video Clip
              </button>
              <button 
                className="flex items-center px-4 py-2 rounded-lg transition-colors bg-blue-500 text-white hover:bg-blue-600"
                onClick={() => {
                  if (isFreePlanUser(currentUserData)) {
                    setShowUpgradeModal({ show: true, feature: 'ai_features' })
                  } else if (canCreateScreens(currentUserData, screens.length, userSubscriptions, subscriptions)) {
                    setShowAdd('dynamic')
                  } else {
                    setShowUpgradeModal({ show: true, feature: 'screens' })
                  }
                }}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Dynamic Content
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {screens.map(screen => (
              <div key={screen.key} className="bg-white rounded-xl border border-gray-200 hover:border-yellow-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                {/* Screen Preview */}
                <div className="relative">
                  <div 
                    className="bg-white w-full overflow-hidden flex items-center justify-center"
                    style={{ 
                      aspectRatio: getAspectRatio(screen)
                    }}
                  >
                    {getScreenThumbById(screen) ? (
                      (screen.angle === 0 || screen.angle === 180 || !screen.angle) ? (
                        <>
                          {/* Landscape TV Frame (0°, 180°, or no angle) */}
                          <div 
                            className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                            style={{
                              borderBottomWidth: (screen.angle === 0 || !screen.angle) ? '20px' : '2px',
                              borderRightWidth: '2px',
                              borderTopWidth: screen.angle === 180 ? '20px' : '2px',
                              borderLeftWidth: '2px',
                              width: '100%',
                              aspectRatio: '16/9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                              background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                              borderColor: '#1f2937',
                              borderStyle: 'solid'
                            }}
                          >
                            <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                              <img
                                src={(() => {
                                  const url = getScreenThumbById(screen);
                                  if (!url) return '';
                                  // Use screen.modified as cache buster if available, else Date.now()
                                  const cacheBuster = screen.modified || Date.now();
                                  return url + (url.includes('?') ? '&' : '?') + 't=' + cacheBuster;
                                })()}
                                alt={screen.name}
                                className="w-full h-full object-contain rounded-lg"
                                style={{
                                  transition: 'transform 0.3s ease',
                                  filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                                }}
                              />
                            </div>
                          </div>
                          {/* TV Power Button - Separate Element */}
                          {(screen.angle === 0 || screen.angle === 180 || !screen.angle) && (
                            <div 
                              className="absolute z-30"
                              style={{
                                bottom: (screen.angle === 0 || !screen.angle) ? '4px' : 'auto',
                                top: screen.angle === 180 ? '4px' : 'auto',
                                left: '50%',
                                transform: 'translateX(-50%)'
                              }}
                            >
                              <div className="w-4 h-4 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full border border-gray-400 shadow-md flex items-center justify-center">
                                <div className="w-1 h-1 bg-red-400 rounded-full shadow-inner"></div>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Portrait TV Frame (90° and 270°) */}
                          <div 
                            className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                            style={{
                              borderBottomWidth: '2px',
                              borderRightWidth: screen.angle === 270 ? '20px' : '2px',
                              borderTopWidth: '2px',
                              borderLeftWidth: screen.angle === 90 ? '20px' : '2px',
                              width: '45%',
                              height: '100%',
                              aspectRatio: '9/16',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                              background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                              borderColor: '#1f2937',
                              borderStyle: 'solid'
                            }}
                          >
                            <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                              <img
                                src={(() => {
                                  const url = getScreenThumbById(screen);
                                  if (!url) return '';
                                  // Use screen.modified as cache buster if available, else Date.now()
                                  const cacheBuster = screen.modified || Date.now();
                                  return url + (url.includes('?') ? '&' : '?') + 't=' + cacheBuster;
                                })()}
                                alt={screen.name}
                                className="w-full h-full object-contain rounded-lg"
                                style={{
                                  transition: 'transform 0.3s ease',
                                  filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                                }}
                              />
                            </div>
                          </div>
                        </>
                      )
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Monitor className="w-16 h-16 text-gray-600" />
                        </div>
                      )}
                  </div>
                  
                  {/* Status indicators */}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    {screen.isDefault && (
                      <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
                        Default
                      </div>
                    )}
                    {screen.isDynamic && (
                      <div className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
                        Dynamic
                      </div>
                    )}
                    {screen.videoUrl && (
                      <div className={`px-2 py-1 rounded-full text-xs font-medium shadow-lg ${
                        screen.isMute ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                      }`}>
                        {screen.isMute ? 'Muted' : 'Audio'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Screen Info */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        {getScreenIcon(screen)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{screen.name}</h3>
                        <p className="text-sm text-gray-500">{getScreenType(screen)}</p>
                        {screen.isDynamic && screen.lastGenerated && (
                          <p className="text-xs text-gray-400">
                            Last updated: {new Date(screen.lastGenerated).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => devices.length > 0 ? handleScheduleClick(screen) : setShowNoDevicesModal(true)}
                        className="flex items-center justify-center px-4 py-2.5 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Schedule
                      </button>
                      <button
                        onClick={() => devices.length > 0 ? setShowAssign(screen) : setShowNoDevicesModal(true)}
                        className="flex items-center justify-center px-4 py-2.5 text-sm font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                      >
                        <Link className="w-4 h-4 mr-2" />
                        Assign
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => setShowPreview(screen)}
                        className="flex items-center justify-center px-3 py-2.5 text-sm font-medium bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                        title="Preview Screen"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowEdit(screen)}
                        className="flex items-center justify-center px-3 py-2.5 text-sm font-medium bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors border border-yellow-200"
                        title="Edit Screen"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowDelete(screen)}
                        className="flex items-center justify-center px-3 py-2.5 text-sm font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors border border-red-200"
                        title="Delete Screen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal.show}
        feature={showUpgradeModal.feature}
        onClose={() => setShowUpgradeModal({ show: false, feature: null })}
      />

      {/* Modals */}
      {showAdd === 'image' && (
        <CreateImageModal
          uid={currentUser?.uid}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            // Refresh screens after creation
            if (typeof setScreens === 'function') {
              // Re-fetch screens using service
              const fetchScreens = async () => {
                const screensData = await screenService.getUserScreens(currentUser?.uid)
                setScreens(screensData)
              }
              fetchScreens()
            }
          }}
        />
      )}

      {showAdd === 'video' && (
        <>
          {console.log('🔍 Rendering CreateVideoModal - showAdd:', showAdd)}
          <CreateVideoModal
            uid={currentUser?.uid}
            currentUserData={currentUserData}
            onClose={() => setShowAdd(false)}
            onCreated={() => {
              // Refresh screens after creation
              if (typeof setScreens === 'function') {
                // Re-fetch screens using service
                const fetchScreens = async () => {
                  const screensData = await screenService.getUserScreens(currentUser?.uid)
                  setScreens(screensData)
                }
                fetchScreens()
              }
            }}
          />
        </>
      )}

      {showAdd === 'slides' && (
        <CreateSlideshowModal
          currentUserData={currentUserData}
          onClose={() => setShowAdd(false)}
          onCreate={() => {
            // Refresh screens after creation
            if (typeof setScreens === 'function') {
              const fetchScreens = async () => {
                const screensData = await screenService.getUserScreens(currentUser?.uid)
                setScreens(screensData)
              }
              fetchScreens()
            }
          }}
        />
      )}

      {showAdd === 'dynamic' && (
        <CreateDynamicContentModal
          uid={currentUser?.uid}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            // Refresh screens after creation
            if (typeof setScreens === 'function') {
              const fetchScreens = async () => {
                const screensData = await screenService.getUserScreens(currentUser?.uid)
                setScreens(screensData)
              }
              fetchScreens()
            }
          }}
        />
      )}

      {showEdit && (
        showEdit.slides && showEdit.slidesData && showEdit.slidesData.length > 0 ? (
          <EditSlideshowModal
            slideshow={showEdit}
            currentUserData={currentUserData}
            onClose={() => setShowEdit(null)}
            onUpdate={() => {
              // Refresh screens after update
              if (typeof setScreens === 'function') {
                const fetchScreens = async () => {
                  const screensData = await screenService.getUserScreens(currentUser?.uid)
                  setScreens(screensData)
                }
                fetchScreens()
              }
              setShowEdit(null)
            }}
          />
        ) : showEdit.videoUrl ? (
          <EditVideoModal
            screen={showEdit}
            onClose={() => setShowEdit(null)}
            onEdit={s => { setShowEdit(null); setScreens(screens.map(x => x.key === s.key ? s : x)) }}
            allScreens={screens}
            screenVersion={showEdit.version || 0}
          />
        ) : (
          <EditImageModal
            screen={showEdit}
            onClose={() => setShowEdit(null)}
            onEdit={s => { setShowEdit(null); setScreens(screens.map(x => x.key === s.key ? s : x)) }}
            allScreens={screens}
            screenVersion={showEdit.version || 0}
          />
        )
      )}

      {showDelete && (
        <ScreenDeleteModal
          screen={showDelete}
          onClose={() => isDeleting ? null : setShowDelete(null)}
          onDelete={handleDelete}
          isDeleting={isDeleting}
          deleteError={deleteError}
        />
      )}

      {showAssign && (
        <ScreenAssignModal
          screen={showAssign}
          devices={devices}
          onClose={() => setShowAssign(null)}
          onAssign={handleAssign}
        />
      )}

      {showSchedule && (
        <ScreenScheduleModal
          screen={showSchedule}
          devices={devices}
          onClose={() => setShowSchedule(null)}
          onAssign={() => setShowAssign(showSchedule)}
        />
      )}

      {showAddSchedule && (
        <AddScheduleModal
          open={true}
          onClose={() => setShowAddSchedule(null)}
          onCreate={() => setShowAddSchedule(null)}
          deviceName={showAddSchedule.device.name || showAddSchedule.device.id}
          screenOptions={screens.map(screen => ({
            label: screen.name,
            value: screen.key,
            thumbUrl: screen.thumbUrl || screen.imageUniversalUrl || screen.imageUrl || '',
            imageUniversalUrl: screen.imageUniversalUrl,
            imageUrl: screen.imageUrl,
            thumbUniversalUrl: screen.thumbUniversalUrl,
            slidesData: screen.slidesData,
            videoUrl: screen.videoUrl
          }))}
          defaultScreen={showAddSchedule.screen.key}
          device={showAddSchedule.device}
          onScheduleAdded={(updatedSchedules) => {
            // Update the device with new schedules
            setDevices(prevDevices => 
              prevDevices.map(device => 
                device.key === showAddSchedule.device.key 
                  ? { ...device, schedule: updatedSchedules }
                  : device
              )
            )
            setShowAddSchedule(null)
          }}
        />
      )}

      {showModifySchedule && (
        <ModifyScheduleModal
          open={true}
          onClose={() => setShowModifySchedule(null)}
          onUpdate={() => setShowModifySchedule(null)}
          deviceName={showModifySchedule.device.name || showModifySchedule.device.id}
          screenOptions={screens.map(screen => ({
            label: screen.name,
            value: screen.key,
            thumbUrl: screen.thumbUrl || screen.imageUniversalUrl || screen.imageUrl || '',
            imageUniversalUrl: screen.imageUniversalUrl,
            imageUrl: screen.imageUrl,
            thumbUniversalUrl: screen.thumbUniversalUrl,
            slidesData: screen.slidesData,
            videoUrl: screen.videoUrl
          }))}
          device={showModifySchedule.device}
          scheduleToEdit={showModifySchedule.scheduleToEdit}
          onScheduleUpdated={(updatedSchedules) => {
            // Update the device with updated schedules
            setDevices(prevDevices => 
              prevDevices.map(device => 
                device.key === showModifySchedule.device.key 
                  ? { ...device, schedule: updatedSchedules }
                  : device
              )
            )
            setShowModifySchedule(null)
          }}
        />
      )}

      {showPreview && (
        <ScreenPreviewModal
          screen={showPreview}
          devices={devices}
          onClose={() => setShowPreview(null)}
        />
      )}

      {showNoDevicesModal && (
        <NoDevicesModal
          onClose={() => setShowNoDevicesModal(false)}
        />
      )}
      
      {showDeviceLimitModal && (
        <DeviceLimitModal
          isOpen={showDeviceLimitModal}
          onClose={() => setShowDeviceLimitModal(false)}
          deviceLimit={getDeviceLimit(currentUserData, userSubscriptions, subscriptions)}
          currentCount={countDevicesRunningScreens(devices)}
        />
      )}


    </div>
  )
}

export default Screens

// --- Modal Components ---



function ScreenDeleteModal({ screen, onClose, onDelete, isDeleting, deleteError }) {
  return (
    <div 
      className="fixed bg-black bg-opacity-60 flex items-center justify-center z-50" 
      style={{ 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        margin: 0, 
        padding: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <h2 className="text-xl font-bold mb-4">Delete Screen</h2>
        <p>Are you sure you want to delete <span className="font-semibold">{screen.name}</span>?</p>
        {deleteError && (
          <div className="text-red-500 mt-3 mb-2 text-sm">{deleteError}</div>
        )}
        {isDeleting && (
          <div className="flex items-center gap-2 mt-4 mb-2 text-yellow-600">
            <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
            Please wait...
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button className="px-4 py-2" onClick={onClose} disabled={isDeleting}>Cancel</button>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded disabled:opacity-50"
            onClick={() => onDelete(screen)}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ScreenAssignModal({ screen, devices, onClose, onAssign }) {
  const [selectedDevice, setSelectedDevice] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = originalOverflow }
  }, [])

          // Filter out channel devices since they can only be assigned to channels
        const availableDevices = devices.filter(device => !device.limited)

  const handleAssign = async () => {
    if (selectedDevice) {
      console.log('Modal: Assigning device:', selectedDevice)
      setIsLoading(true)
      try {
        await onAssign(selectedDevice)
      } catch (error) {
        console.error('Assignment failed:', error)
        alert(`Assignment failed: ${error.message}`)
      } finally {
        setIsLoading(false)
      }
    }
  }

  // Check if device is online
  const isOnline = (device) => device.lastAccess && (Date.now() - device.lastAccess < 360000)

  return (
    <div 
      className="fixed bg-black bg-opacity-30 flex items-center justify-center z-50" 
      style={{ 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        margin: 0, 
        padding: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Assign Screen</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600 mb-2">Select a device to assign:</p>
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Monitor className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{screen.name}</h3>
                <p className="text-sm text-gray-500">{screen.videoUrl ? 'Video' : screen.slides ? 'Slideshow' : 'Single Image'}</p>
              </div>
            </div>
          </div>
        </div>

        {availableDevices.length === 0 ? (
          <div className="text-center py-8">
            <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-2">No devices available for screen assignment</p>
            <p className="text-sm text-gray-500">
              {devices.length === 0 
                ? 'Add a device first to assign this screen'
                : 'All available devices are channel devices and can only be assigned to channels'
              }
            </p>
          </div>
        ) : (
          <div className="mb-4">
            <label htmlFor="device-select" className="block text-sm font-medium text-gray-700 mb-2">
              Choose Device
            </label>
            <select
              id="device-select"
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white"
            >
              <option value="">Select a device...</option>
              {availableDevices.map(device => (
                <option key={device.key} value={device.key}>
                  {device.name} ({device.manufacturer} {device.model}) - {isOnline(device) ? 'Online' : 'Offline'}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button 
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleAssign}
            disabled={!selectedDevice || isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Assigning...
              </>
            ) : (
              <>
                <Link className="w-4 h-4" />
                Assign Screen
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

function ScreenScheduleModal({ screen, devices, onClose, onAssign }) {
  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = originalOverflow }
  }, [])

  return (
    <div 
      className="fixed bg-black bg-opacity-30 flex items-center justify-center z-50" 
      style={{ 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        margin: 0, 
        padding: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <h2 className="text-xl font-bold mb-4">Schedule Screen</h2>
        <div className="text-center py-8">
          <div className="mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Device Assigned</h3>
            <p className="text-gray-600 mb-4">
              To schedule "{screen.name}", you need to assign it to a device first.
            </p>
          </div>
          <div className="flex gap-3 justify-center">
            <button 
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
              onClick={() => {
                onClose() // Close this modal first
                onAssign() // Then open the assign modal
              }}
            >
              <Link className="w-4 h-4" />
              Assign Device
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function NoDevicesModal({ onClose }) {
  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = originalOverflow }
  }, [])

  return (
    <div 
      className="fixed bg-black bg-opacity-60 flex items-center justify-center z-50" 
      style={{ 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        margin: 0, 
        padding: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Smartphone className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Devices Available</h3>
          <p className="text-gray-600 mb-6">
            You need to add a device first before you can assign screens or create schedules.
          </p>
          <div className="flex gap-3 justify-center">
            <button 
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={onClose}
            >
              Close
            </button>
            <button
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-2"
              onClick={() => {
                onClose()
                // Navigate to devices page
                window.location.href = '/devices'
              }}
            >
              <Smartphone className="w-4 h-4" />
              Go to Devices
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 